import { shiftDate, today } from '../../core/format/dates'
import { products } from '../../data/products'
import { photoConsent, policy } from '../../data/policies'
import type { Account, Customer } from '../../core/types/account'

/**
 * DEMO — bouwstenen voor voorbeeldaccounts. Alleen gebruikt binnen demo/;
 * met een backend verdwijnt de hele map.
 */

/** Datum relatief tot vandaag, als "YYYY-MM-DD". */
export const day = (offset: number) => shiftDate(today(), offset)

export const baseCustomer = (
  patch: Partial<Customer> & Pick<Customer, 'id' | 'firstName' | 'lastName' | 'email'>,
): Customer => ({
  // Voorbeeldklanten zijn al lang klant: bevestigd. Nieuwe accounts zetten dit zelf.
  emailVerified: true,
  // Voorbeeldklanten loggen in met DEMO_PASSWORD. Nieuwe accounts zetten dit zelf.
  hasPassword: true,
  phone: '',
  address: { street: '', houseNumber: '', postcode: '', city: '', country: 'NL' },
  payment: { method: 'ideal', label: 'Wero' },
  prefs: { refillWhatsApp: true, refillEmail: false, newsletter: false },
  diaryConsent: false,
  // Voorbeeldklanten met foto's gaven al toestemming; nieuwe accounts krijgen `null`.
  photoStorageConsent: photoConsent.version,
  memberSince: day(0),
  ...patch,
})

/** Een leeg huidplan dat op een datum begint. */
export const emptyPlan = (startedOn: string) => ({ startedOn, focus: '', note: '', items: [] })

/**
 * Een bestelregel bewaart de prijs zoals betaald (facturen!). In de demo
 * vullen we de huidige prijs in.
 */
export const withPaidPrices = (account: Account): Account => ({
  ...account,
  orders: account.orders.map((o) => ({
    ...o,
    lines: o.lines.map((l) => ({ ...l, price: l.price || products.find((p) => p.id === l.productId)?.price || 0 })),
  })),
})

/**
 * Een nieuw, leeg account — bij registreren of via een inloglink. Bestellingen horen er niet
 * in: die staan in de ordertabel (demo/db.ts) en worden via `customerId` gekoppeld.
 */
export function newDemoAccount(input: {
  firstName: string
  /** Leeg = aangemaakt door Sarah zonder e-mailadres (dan staat `phone` ingevuld). */
  email: string
  lastName?: string
  phone?: string
  /** Registreren: `true`. Via een inloglink of door Sarah aangemaakt: `false` (nog geen wachtwoord). */
  hasPassword: boolean
  subscribeIds?: string[]
  /** Het ritme dat bij het afrekenen gekozen werd; alleen een toegestaan ritme telt. */
  intervalWeeks?: number
  emailVerified?: boolean
}): Account {
  const start = day(0)
  const subscribeIds = input.subscribeIds ?? []
  const chosen = input.intervalWeeks
  const weeks = chosen !== undefined && policy.subscription.intervals.includes(chosen) ? chosen : policy.subscription.defaultIntervalWeeks
  return {
    customer: baseCustomer({
      // Met een willekeurig achtervoegsel: twee klanten in dezelfde milliseconde (samenvoegen, import) botsen niet.
      id: `klant-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      firstName: input.firstName.trim(),
      lastName: (input.lastName ?? '').trim(),
      email: input.email.trim().toLowerCase(),
      phone: (input.phone ?? '').trim(),
      emailVerified: input.emailVerified ?? false,
      hasPassword: input.hasPassword,
      photoStorageConsent: null,
    }),
    plan: emptyPlan(start),
    subscription: subscribeIds.length
      ? {
          id: `ab-${Date.now().toString(36)}`,
          status: 'actief',
          intervalWeeks: weeks,
          nextDelivery: day(weeks * 7),
          items: subscribeIds.map((productId) => ({ productId, qty: 1 })),
          extras: [],
          deliveriesDone: 1,
        }
      : null,
    appointments: [],
    visits: [],
    course: null,
    orders: [],
    diary: [],
  }
}
